import { create } from "zustand";
import Plivo from "plivo-browser-sdk";
import { getVoiceToken } from "../api/voiceApi";

const IDLE = {
  status: "idle",
  callId: null,
  toNumber: null,
  contactName: null,
  answeredAt: null,
  muted: false,
  error: null,
  onFinished: null,
};

const FAILURE_MESSAGES = {
  INVALID_ACCESS_TOKEN: "Calling is not available for your account right now.",
  ACCESS_TOKEN_EXPIRED: "Your calling session expired. Please try again.",
  MAX_ALLOWED_LOGIN_REACHED: "Calling is already open in another tab.",
  "Not Found": "The call could not be connected.",
  Busy: "The line is busy.",
  Rejected: "The call was rejected.",
};

// One Plivo client per tab; it lives outside React so navigating does not drop a call.
let plivo = null;
let loginPromise = null;

export const useCallStore = create((set, get) => {
  const finish = (status, error = null) => {
    const { onFinished, callId, status: current } = get();
    // The SDK can report failed and terminated for the same call; settle it once.
    if (["idle", "ended", "failed"].includes(current)) return;
    set({ status, error });
    onFinished?.({ callId, status });
    // Log out after every call so idle tabs do not keep refreshing Plivo tokens.
    plivo?.client.logout();
    loginPromise = null;
    // Long enough to still click "Do not call again" right after hanging up.
    window.setTimeout(() => {
      if (get().callId === callId && ["ended", "failed"].includes(get().status)) set(IDLE);
    }, 15000);
  };

  const ensureClient = () => {
    if (plivo) return plivo.client;
    plivo = new Plivo({
      debug: "ERROR",
      permOnClick: true,
      enableTracking: true,
      closeProtection: true,
      maxAverageBitrate: 48000,
    });
    const client = plivo.client;
    client.on("onCallRemoteRinging", () => set({ status: "ringing" }));
    client.on("onCallAnswered", () => set({ status: "in_call", answeredAt: Date.now() }));
    client.on("onCallTerminated", () => finish("ended"));
    client.on("onCallFailed", (cause) => finish("failed", FAILURE_MESSAGES[cause] || "The call could not be connected."));
    client.on("onMediaPermission", (event) => {
      if (event?.status === "failure") {
        set({ error: "Allow microphone access in your browser to place calls." });
      }
    });
    client.on("onLogout", () => {
      loginPromise = null;
    });
    window.addEventListener("beforeunload", () => plivo?.client.logout());
    return client;
  };

  const login = (client) => {
    if (client.isLoggedIn) return Promise.resolve();
    if (loginPromise) return loginPromise;
    loginPromise = new Promise((resolve, reject) => {
      const cleanup = () => {
        client.removeListener("onLogin", onLogin);
        client.removeListener("onLoginFailed", onLoginFailed);
      };
      const onLogin = () => {
        cleanup();
        resolve();
      };
      const onLoginFailed = (cause) => {
        cleanup();
        loginPromise = null;
        reject(new Error(FAILURE_MESSAGES[cause] || "Could not connect to the calling service."));
      };
      client.on("onLogin", onLogin);
      client.on("onLoginFailed", onLoginFailed);
      // The SDK refreshes the short-lived token itself; a null token makes it emit onLoginFailed.
      client.loginWithAccessTokenGenerator({
        getAccessToken: () =>
          getVoiceToken()
            .then((result) => result?.token || null)
            .catch(() => null),
      });
    });
    return loginPromise;
  };

  return {
    ...IDLE,

    async startCall({ callId, toNumber, contactName, onFinished }) {
      if (!["idle", "ended", "failed"].includes(get().status)) {
        throw new Error("Another call is already in progress.");
      }
      set({ ...IDLE, status: "connecting", callId, toNumber, contactName, onFinished });
      try {
        const client = ensureClient();
        await login(client);
        // The user may have hung up while we were still logging in.
        if (get().callId !== callId || get().status !== "connecting") return;
        const placed = client.call(toNumber, { "X-PH-CallId": callId });
        if (!placed) finish("failed", "The call could not be started.");
      } catch (err) {
        finish("failed", err?.message || "The call could not be started.");
      }
    },

    hangup() {
      if (get().status === "connecting" && !plivo?.client.getCallUUID()) {
        finish("ended");
        return;
      }
      plivo?.client.hangup();
    },

    toggleMute() {
      const client = plivo?.client;
      if (!client) return;
      const muted = !get().muted;
      if (muted) client.mute();
      else client.unmute();
      set({ muted });
    },

    dismiss() {
      if (["ended", "failed"].includes(get().status)) set(IDLE);
    },
  };
});
